import {
  Alert,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import PageSection from 'components/common/PageSection'
import {
  AuthenticationType,
  getBinaryRadioOptions
} from 'components/status-checks/authentication-form-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { useTranslation } from 'hooks/use-translation'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'
import { PRIVATE_NETWORK_INTEGRATION_OBSERVABILITY_TOOLS } from 'utils/third-party'

import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import type { PrivateNetworkIntegrationClient } from 'types/api/integration-clients/integration-clients'

/**
 * Component for configuring private network integration settings
 * @see {@link PRIVATE_NETWORK_INTEGRATION_OBSERVABILITY_TOOLS} for tools that support private network integration
 */
export const ObservabilityToolPrivateNetworkIntegration: React.FC = () => {
  const { credentialRetrieval: isCredentialRetrievalEnabled } =
    useFeatureFlags()

  const { language, trans } = useTranslation()
  const { control, resetField, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()
  const { isEdit } = useObservabilityToolAuthenticationContext()

  const isPrivateNetworkIntegration = useWatch<StatusCheckFormValues>({
    control,
    name: 'isPrivateNetworkIntegration'
  })

  const {
    data: activeIntegrationClients,
    isLoading: isActiveIntegrationClientsLoading,
    isError: hasActiveIntegrationClientsError
  } = useGremlinQuery<PrivateNetworkIntegrationClient[]>(
    '/integration-clients/active',
    {},
    { enabled: isPrivateNetworkIntegration }
  )

  const shouldShowPrivateIntegrationAlert = useMemo(
    () =>
      isPrivateNetworkIntegration &&
      !activeIntegrationClients?.length &&
      !isActiveIntegrationClientsLoading,
    [
      activeIntegrationClients?.length,
      isActiveIntegrationClientsLoading,
      isPrivateNetworkIntegration
    ]
  )

  const binaryRadioOptions = getBinaryRadioOptions(language)

  const [authenticationType, secret, selectedObservabilityTool] = useWatch({
    control,
    name: ['authenticationType', 'secret', 'observabilityTool']
  })

  return (
    <FormControl>
      <Typography mb={1}>
        {trans(
          'forms.save_status_checks.custom_observability_tool_options.is_private_network.prompt'
        )}
      </Typography>
      <Controller
        control={control}
        name="isPrivateNetworkIntegration"
        render={({ field }) => (
          <RadioGroup
            {...field}
            onChange={(e) => {
              const isPrivate = e.target.value === 'true'
              // this is necessary because radio button values are converted to strings at the DOM level
              field.onChange(isPrivate)
              // we need to revalidate `apiBaseUrl` since PNI affects the apiBaseUrl validation schema
              trigger('apiBaseUrl')

              if (!isPrivate) {
                secret && resetField('secret')
                resetField('authenticationType')
              }

              // TODO EN-9978 - use PRIVATE_NETWORK_INTEGRATION_OBSERVABILITY_TOOLS.has(observabilityToolType) when we are done with adding arn support for all the auths
              const hasMultipleAuthSupport =
                isCredentialRetrievalEnabled &&
                selectedObservabilityTool.type !==
                  ObservabilityToolType.DATADOG &&
                selectedObservabilityTool.type !== ObservabilityToolType.AWS

              if (hasMultipleAuthSupport && !authenticationType) {
                setValue(
                  'authenticationType',
                  // auth is optional for Prometheus, we will let user explicitly set the auth type
                  // for other tools that support different auth types, default to Authorization Header
                  selectedObservabilityTool.type ===
                    ObservabilityToolType.PROMETHEUS
                    ? undefined
                    : AuthenticationType.AuthorizationHeader
                )
              }
            }}
          >
            {binaryRadioOptions.map(({ label, value }, index) => (
              <FormControlLabel
                key={`${value}-${index}`}
                control={
                  <Radio
                    data-test-observability-tool-private-network-integration={
                      value
                    }
                    disabled={isEdit}
                  />
                }
                label={
                  <Stack
                    direction="row"
                    gap={3}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Typography>{label}</Typography>
                    {shouldShowPrivateIntegrationAlert && value && (
                      <PageSection
                        hasError={hasActiveIntegrationClientsError}
                        isLoading={isActiveIntegrationClientsLoading}
                        renderError={() => (
                          <Alert severity="error">
                            {trans(
                              'forms.save_status_checks.custom_observability_tool_options.is_private_network.alert.error.text'
                            )}{' '}
                            <Link
                              href="https://www.gremlin.com/docs/platform/integration-agent/"
                              target="_blank"
                            >
                              {trans(
                                'forms.save_status_checks.custom_observability_tool_options.is_private_network.alert.link.text'
                              )}
                            </Link>
                          </Alert>
                        )}
                      >
                        <Alert severity="warning">
                          {trans(
                            'forms.save_status_checks.custom_observability_tool_options.is_private_network.alert.line_1.text'
                          )}
                          <Link
                            href="https://www.gremlin.com/docs/platform/integration-agent/"
                            target="_blank"
                          >
                            {trans(
                              'forms.save_status_checks.custom_observability_tool_options.is_private_network.alert.link.text'
                            )}
                          </Link>
                        </Alert>
                      </PageSection>
                    )}
                  </Stack>
                }
                sx={{ width: 'fit-content' }}
                value={value}
              />
            ))}
          </RadioGroup>
        )}
      />
    </FormControl>
  )
}
